import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="19634" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-full" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="bg-transparent hover:bg-accent/30 focus:bg-accent/30 text-foreground/90 hover:text-primary font-medium tracking-normal transition-all hover:underline underline-offset-8 decoration-accent/60">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-8 w-full bg-muted animate-pulse rounded-full" />)}
      </div>;
  }
  return <nav className="flex flex-col p-6 space-y-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium tracking-normal text-foreground hover:text-primary transition-colors py-2 border-b border-border/30" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function ClinicHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className="sticky top-0 z-50 w-full transition-all duration-500 ease-in-out border-b border-transparent !bg-[hsl(0,0%,100%)]">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between !bg-[hsl(0,0%,100%)]">
        {}
        <div className="flex-shrink-0 mx-auto">
          <Link to="/HomePage" className="block">
            <img src="https://fair-mollusk.10web.cloud/wp-content/uploads/2026/04/Crescent-Logo.png" alt="Crescent Direct Primary Care" className="w-auto object-contain transition-transform duration-300 !h-[44px] !bg-[hsl(0,0%,100%)] hover:scale-105" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="43" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-6">
          <Link to="tel:(682) 800-4727" className="flex items-center gap-2 text-secondary font-medium hover:text-secondary/80 transition-colors text-sm tracking-wide">
            <Phone className="h-4 w-4" />
            <span>Phone: (682) 800-4727</span>
          </Link>
          <Button asChild className="bg-primary hover:bg-primary/95 text-primary-foreground font-medium tracking-wide shadow-lg rounded-full px-6 transition-all hover:shadow-xl hover:-translate-y-0.5">
            <Link to="/RegisterPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-9 py-2 has-[>svg]:px-3 text-primary-foreground font-medium tracking-wide shadow-lg rounded-full px-6 transition-all !bg-[hsl(223,23%,43%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/95 hover:shadow-xl hover:-translate-y-0.5">Join Practice</Link>
          </Button>
        </div>

        {}
        <div className="flex lg:hidden items-center gap-4">
           {}
           <Link to="tel:(682)800-4727" className="flex md:hidden items-center justify-center text-secondary p-2" aria-label="Call Practice">
            <Phone className="h-5 w-5" />
          </Link>
          
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:bg-accent/50 rounded-full" aria-label="Open menu">
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 lg:hidden">
          {}
          <div className="absolute inset-0 bg-primary/20 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="absolute right-0 top-0 h-full w-[320px] bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 ease-out-expo">
            <div className="flex items-center justify-between p-6 border-b border-border/50">
              <span className="font-serif text-2xl tracking-tight text-primary">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="rounded-full hover:bg-accent/50">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="43">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="p-8 border-t border-border/50 space-y-6 bg-accent/10">
              <Button asChild className="w-full bg-primary hover:bg-primary/95 text-primary-foreground rounded-full shadow-md py-6">
                <Link to="/RegisterPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Join Practice
                </Link>
              </Button>
              
              <div className="text-center">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Patient Access</p>
                <Link to="tel:(555)123-4567" className="text-secondary font-bold flex items-center justify-center gap-2 text-lg"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone h-4 w-4" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:so6iey" data-ast-start="8371" data-ast-end="8400"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg>(682) 800-4727</Link>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}